import { Box, Collapse, SxProps } from '@mui/material';
import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldErrors, useFormContext, useWatch } from 'react-hook-form';
import { styles } from '../../styles';
import { OwnerType, TOwner, TOwnerNode, TOwnershipStructure } from '../../types';
import { getChildIndexes, getOwnerName } from '../../utils/utils.tsx';
import { AddOwnerButton } from '../AddOwnerButton.tsx';
import { EmptyOwnersList } from './EmptyOwnersList.tsx';
import { TextInput } from '~/components/TextField/TextInput.tsx';
import { useSortable } from '@dnd-kit/react/sortable';
import { OwnerCardView } from './OwnerCardView.tsx';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox.tsx';
import { ErrorInfoMessage } from './ErrorInfoMessage.tsx';
import { TGeneralInfoLegalResident } from '../../../GeneralInfo/types.ts';
import { useSelector } from 'react-redux';
import {
  areMirrorInstances,
  collectSubtreeIds,
  edgeBandCollision,
} from '../../utils/collisionUtils.ts';
import { ROOT_OWNER_NODE } from '../../constants.ts';
import {
  selectGeneralInfo,
  selectGettingStartedIdentNumber,
} from '~/store/identification-form/selectors.ts';
import { roundNumber } from '~/helpers/utils.ts';

interface IOwnerCardProps {
  owner: TOwnerNode;
  index: number;
  handleOpen: (index: number) => void;
  onDelete: (index: number) => void;
  handleAddItem: (item: OwnerType, parentId?: string, parentLinkId?: string | null) => void;
  activeLinkId: string | null;
  setDeleteFunc: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
  dropParent: { linkId: string | null; allowed: boolean } | null;
}

export const OwnerCard = React.memo(
  ({
    owner,
    index: sortIndex,
    handleOpen,
    onDelete,
    handleAddItem,
    setDeleteFunc,
    activeLinkId,
    dropParent,
  }: IOwnerCardProps) => {
    const {
      control,
      getValues,
      setValue,
      trigger,
      formState: { errors },
    } = useFormContext<TOwnershipStructure>();

    const { fullNameUkr } = (useSelector(selectGeneralInfo) as TGeneralInfoLegalResident) || {};
    const generalIdentificationNumber = useSelector(selectGettingStartedIdentNumber) || {};

    const { fieldIndex: index, linkIndex, linkId, parentLinkId, level } = owner;

    const [currentLink, links, owners] = useWatch({
      control,
      name: [`link.${linkIndex}`, 'link', 'owners'],
    });

    const { type, isBeneficiary, areIndividualsWithDecisiveInfluenceAbsent } =
      owners?.[index] || {};

    const isLegal = type === OwnerType.LEGAL_ENTITY;
    const isIndividual = type === OwnerType.INDIVIDUAL;
    const isDropTarget = dropParent?.linkId === linkId;
    const isDropDisabled = isDropTarget && !dropParent.allowed;
    const ownershipPersentage = useMemo(
      () =>
        roundNumber(
          links
            .filter((link) => link.parentLinkId === currentLink.id)
            .reduce((item, currentOwner) => item + Number(currentOwner.ownershipPercentage || 0), 0)
        ),
      [links, owner.id]
    );
    const parentOwner = useMemo(
      () => owners.find((owner) => currentLink?.toNodeId === owner.id),
      [owners, currentLink?.toNodeId]
    );
    const isExpandable =
      (isLegal && !owner.isCyclic) ||
      (isIndividual &&
        !isBeneficiary &&
        parentOwner?.type !== OwnerType.INDIVIDUAL &&
        !!level &&
        level > 1 &&
        level < 20);

    const { ref, handleRef, isDragging } = useSortable({
      id: linkId,
      index: sortIndex,
      group: parentLinkId || ROOT_OWNER_NODE,
      collisionDetector: edgeBandCollision,
      data: {
        linkIndex,
        parentLinkId,
        level,
        data: {
          linkIndex,
          parentLinkId,
          level,
          isParentCard: isLegal && !areIndividualsWithDecisiveInfluenceAbsent,
        },
      },
    });

    const [expanded, setExpanded] = useState(false);

    const { indexes: childOwnerIndexes, linkIndexes: childLinkIndexes } = useMemo(
      () => getChildIndexes(owner),
      [owner]
    );

    const ownerErrors = errors.owners;
    const linkError = errors.link;
    const percentageError = (
      ownerErrors?.[index] as FieldErrors<TOwner & { ownershipPercentageTotal: string }>
    )?.ownershipPercentageTotal?.message;
    const childError =
      childOwnerIndexes?.some((childIndex) => ownerErrors?.[childIndex]) ||
      childLinkIndexes?.some((childIndex) => linkError?.[childIndex]) ||
      !!percentageError;

    const showError = !!ownerErrors?.[index] || !!linkError?.[linkIndex] || childError;
    const ownerErrorMessage = (ownerErrors?.[index] as FieldErrors<TOwner & { noOwners: string }>)
      ?.noOwners?.message;
    const showAddBtn =
      (isIndividual ? !childOwnerIndexes.length : isLegal) && !!level && level < 20;
    const ownerLinks = links.filter((link) => link.fromNodeId === owner.id);

    const parentNames = useMemo(
      () =>
        ownerLinks.map((link) => {
          const parent = owners.find((owner) => owner.id === link.toNodeId);
          const name = getOwnerName(parent) || fullNameUkr;
          const numberType = parent && parent?.type === OwnerType.INDIVIDUAL ? 'РНОКПП' : 'ЄДРПОУ';
          const identificationNumber =
            parent?.identificationNumber ?? parent?.foreignTaxNumber ?? generalIdentificationNumber;
          return [name, numberType, identificationNumber].join(' ');
        }),
      [ownerLinks, fullNameUkr, owners]
    );

    const activeOwner = useMemo(() => {
      if (!activeLinkId) return undefined;
      const activeLink = links.find((link) => link.id === activeLinkId);
      return owners.find((owner) => owner.id === activeLink?.fromNodeId);
    }, [activeLinkId, links, owners]);

    useEffect(() => {
      if (activeLinkId) {
        const isHoveredDropTarget = dropParent?.linkId === linkId && dropParent.allowed;
        const shouldExpand =
          activeLinkId !== linkId &&
          activeOwner?.id !== owner.id &&
          (!isIndividual || activeOwner?.type === OwnerType.INDIVIDUAL) &&
          isHoveredDropTarget &&
          !expanded &&
          isExpandable &&
          activeOwner?.type !== OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP;

        if (shouldExpand) {
          setExpanded(true);
        }
      }

      if ((!isExpandable && expanded) || (isBeneficiary && isIndividual)) {
        setExpanded(false);
      }
    }, [isExpandable, activeLinkId, linkId, activeOwner, isBeneficiary, isIndividual]);

    const handleExpandClick = () => {
      if (!isExpandable) return;
      setExpanded(!expanded);
    };

    const handleDelete = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onDelete(linkIndex);
    };

    const handleEdit = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      handleOpen(linkIndex);
    };

    const handleAdd = useCallback(
      (type: OwnerType) => {
        const ownerId = getValues(`owners.${index}.id`);
        handleAddItem(type, ownerId, linkId);
      },
      [getValues, index, handleAddItem, linkId]
    );

    const handleDeleteChildrens = () => {
      const allLinks = getValues('link');
      const allOwners = getValues('owners');

      const thisLink = allLinks.find((l) => l.id === linkId);
      if (!thisLink) return;

      const instances = allLinks.filter(
        (l) => l.id === thisLink.id || areMirrorInstances(l, thisLink, allLinks, allOwners)
      );

      const idsToRemove = new Set<string>();
      for (const inst of instances) {
        allLinks
          .filter((l) => l.parentLinkId === inst.id)
          .forEach((child) =>
            collectSubtreeIds(child.id, allLinks).forEach((id) => idsToRemove.add(id))
          );
      }

      const remainingLinks = allLinks.filter((l) => !idsToRemove.has(l.id));
      const referencedOwnerIds = new Set(remainingLinks.flatMap((l) => [l.fromNodeId, l.toNodeId]));
      const remainingOwners = allOwners.filter((o) => referencedOwnerIds.has(o.id));

      setValue('link', remainingLinks, { shouldDirty: true });
      if (remainingOwners.length !== allOwners.length) {
        setValue('owners', remainingOwners, { shouldDirty: true });
      }

      const ownerIndex = remainingOwners.findIndex((o) => o.id === owner.id);
      if (ownerIndex !== -1) {
        setValue(`owners.${ownerIndex}.areIndividualsWithDecisiveInfluenceAbsent`, true, {
          shouldDirty: true,
        });
        trigger(`owners.${ownerIndex}`);
      }
    };

    const handleCheckboxChange = (isChecked: boolean) => {
      if (isChecked && owner.children.length) {
        onDelete(-2);
        setDeleteFunc(() => handleDeleteChildrens);
        return;
      }
      setValue(`owners.${index}.areIndividualsWithDecisiveInfluenceAbsent`, isChecked, {
        shouldDirty: true,
      });
      trigger(`owners.${index}`);
    };

    return (
      <Box
        ref={ref}
        sx={{
          opacity: isDragging ? 0.35 : 1,
          transition: 'opacity 0.15s',
        }}
        data-link-id={currentLink.id}
      >
        <OwnerCardView
          owner={owner}
          handleRef={handleRef}
          isExpandable={isExpandable}
          currentLink={currentLink}
          expanded={expanded}
          handleDelete={handleDelete}
          handleEdit={handleEdit}
          handleExpandClick={handleExpandClick}
          showError={showError}
          childError={childError}
          parents={parentNames}
        >
          <Collapse
            in={expanded}
            timeout="auto"
            unmountOnExit
            component={Box}
            sx={styles.collapseContainer}
          >
            {isLegal && (
              <FormCheckbox
                name={`owners.${index}.areIndividualsWithDecisiveInfluenceAbsent`}
                label="Відсутні фізичні особи, які мають можливість здійснювати вирішальний вплив на управління або господарську діяльність"
                disableFieldOnChange
                onChange={handleCheckboxChange}
              />
            )}
            {(!areIndividualsWithDecisiveInfluenceAbsent || !isLegal) && (
              <>
                <Box
                  sx={
                    styles.dropZoneContainer({
                      isOver: dropParent?.linkId === currentLink.id,
                      isDisabled: isDropDisabled,
                    }) as SxProps
                  }
                >
                  {owner.children.length ? (
                    owner.children.map((item, childIndex) => (
                      <OwnerCard
                        key={item.linkId}
                        owner={item}
                        index={childIndex}
                        handleOpen={handleOpen}
                        onDelete={onDelete}
                        handleAddItem={handleAddItem}
                        setDeleteFunc={setDeleteFunc}
                        activeLinkId={activeLinkId}
                        dropParent={dropParent}
                      />
                    ))
                  ) : (
                    <EmptyOwnersList isIndividual={isIndividual} />
                  )}
                </Box>
                {ownerErrorMessage && <ErrorInfoMessage error={ownerErrorMessage} />}
                {isLegal && (
                  <TextInput
                    sx={{ width: '50%' }}
                    disabled
                    value={ownershipPersentage}
                    label={'Загальна частка всіх учасників у статутному капіталі (%)'}
                    error={!!percentageError}
                    helperText={percentageError}
                    slotProps={{
                      htmlInput: {
                        'data-testid': `ownershipPercentageTotal-${ownershipPersentage}`,
                      },
                      formHelperText: {
                        'data-testid': `ownershipPercentageTotal-${percentageError}`,
                      } as React.HTMLAttributes<HTMLParagraphElement>,
                    }}
                  />
                )}
                {showAddBtn && (
                  <AddOwnerButton
                    handleAddOwner={handleAdd}
                    parentId={owner.id}
                    parentLinkId={linkId}
                    level={owner.level}
                    isIndividualParent={isIndividual}
                    title={isIndividual ? 'Додати Фізичну особу' : 'Додати Власника'}
                  />
                )}
              </>
            )}
          </Collapse>
        </OwnerCardView>
      </Box>
    );
  }
);
